import { useTranslation } from "react-i18next";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps/dist/index.umd";
import { Box, IconButton, Stack, Typography } from "@material-ui/core";
import { MyLocation } from "@material-ui/icons";
import type { Map as MapTypes, Marker as MarkerTypes } from "@vis.gl/react-google-maps";
import type { AddressCoordinates } from "../../../../../@types/listings/new";

type GoogleMapsCoordinates = google.maps.LatLngLiteral;

export type GoogleMapMouseEvent = google.maps.MapMouseEvent;

export type ListingInteractiveMapBaseProps = {
  addressCoordinates: AddressCoordinates | null;
  onAddressCoordinatesChange: (event: GoogleMapMouseEvent) => void;
};

type ParsedListingInteractiveMapBaseProps = Pick<
  ListingInteractiveMapBaseProps,
  "addressCoordinates"
> &
  Partial<ListingInteractiveMapBaseProps>;

type ListingInteractiveMapProps = ParsedListingInteractiveMapBaseProps & {
  readonly?: boolean;
  allowDefaultCoordinates?: boolean;
  /** Skip the built-in heading — for hosts that already render their own title
   *  (e.g. the address picker's "location on map" accordion summary). */
  hideTitle?: boolean;
};

// Default coordinates for israel if allowDefaultCoordinates is true
export const defaultAddressCoordinatesMap: AddressCoordinates = {
  lat: 31.023627119132147,
  lon: 34.83942504461794
};

const GoogleMaps = Map as typeof MapTypes;
const GoogleMarker = Marker as typeof MarkerTypes;

const useGoogleMaps = useMap as () => google.maps.Map | null;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";

/**
 * The READ-ONLY map (a listing's page in the dashboard and on the client's public page) is Google's Maps
 * Embed iframe, which Google does not bill, instead of the interactive Maps JavaScript map, which is billed
 * per load past 10,000 a month (2026-10-24: ≈ 12,300 loads a month, almost all of them this map — the cost
 * plan's A9). The client still pans and zooms; only the pin can't be dragged, which was already the case.
 * The address pickers (not readonly) keep the interactive map, because there the pin IS dragged.
 * The key must allow `maps-embed-backend.googleapis.com` (added 2026-10-24) — an embed with a key that
 * lacks it shows Google's error box instead of the map.
 */
const embedMapUrl = (coordinates: GoogleMapsCoordinates, zoom: number, hasPin: boolean, language: string): string => {
  const at = `${coordinates.lat},${coordinates.lng}`;
  const params = new URLSearchParams({ key: GOOGLE_MAPS_API_KEY, zoom: String(zoom), language });
  if (hasPin) params.set("q", at);
  else params.set("center", at);
  return `https://www.google.com/maps/embed/v1/${hasPin ? "place" : "view"}?${params.toString()}`;
};

export function ListingInteractiveMap({
  readonly,
  hideTitle,
  addressCoordinates,
  allowDefaultCoordinates,
  onAddressCoordinatesChange
}: ListingInteractiveMapProps) {
  const map = useGoogleMaps();

  const { t: translate, i18n } = useTranslation();

  let parsedAddressCoordinates = addressCoordinates;
  let parsedMapZoom = 16;

  if (!parsedAddressCoordinates && allowDefaultCoordinates) {
    parsedMapZoom = 7;
    parsedAddressCoordinates = defaultAddressCoordinatesMap;
  }

  const googleMapCoordinates = parseMapCoordinates(parsedAddressCoordinates);

  const handleRecenterMap = (): void => {
    if (!map || !googleMapCoordinates) return;

    map.panTo(googleMapCoordinates);
  };

  if (readonly) {
    // Google's embed takes "iw" for Hebrew.
    const mapLanguage = !i18n.language || i18n.language.startsWith("he") ? "iw" : i18n.language;
    return (
      <Stack gap={2}>
        {!hideTitle && <Typography variant="h6">{translate("listingsPage.locationOnMap")}</Typography>}
        {googleMapCoordinates ? (
          <Box
            style={{
              width: "100%",
              height: 320,
              position: "relative",
              overflow: "hidden",
              borderRadius: 16
            }}
          >
            <iframe
              title={translate("listingsPage.locationOnMap")}
              src={embedMapUrl(googleMapCoordinates, parsedMapZoom, !!addressCoordinates, mapLanguage)}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        ) : (
          <Typography variant="subtitle1" color="textSecondary">
            {translate("listingsPage.error.noMapAvailableForThisAddress")}
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {!hideTitle && <Typography variant="h6">{translate("listingsPage.locationOnMap")}</Typography>}
      {googleMapCoordinates ? (
        <Box
          style={{
            width: "100%",
            height: 320,
            position: "relative",
            overflow: "hidden",
            borderRadius: 16
          }}
        >
          <GoogleMaps defaultZoom={parsedMapZoom} defaultCenter={googleMapCoordinates} disableDefaultUI>
            <GoogleMarker draggable position={googleMapCoordinates} onDragEnd={onAddressCoordinatesChange} />
          </GoogleMaps>
          <IconButton
            sx={{
              bgcolor: "white",
              position: "absolute",
              top: 8,
              right: 8,
              "&:hover": { bgcolor: "white" }
            }}
            size="small"
            onClick={handleRecenterMap}
          >
            <MyLocation />
          </IconButton>
        </Box>
      ) : (
        <Typography variant="subtitle1" color="textSecondary">
          {translate("listingsPage.error.noMapAvailableForThisAddress")}
        </Typography>
      )}
    </Stack>
  );
}

function parseMapCoordinates(coordinates?: AddressCoordinates): GoogleMapsCoordinates | null {
  if (!coordinates) return null;

  return {
    lat: coordinates.lat,
    lng: coordinates.lon
  };
}
